import Link from './LocalizedLink'
import { Mail, Phone, MapPin, Linkedin, Youtube } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'

const translations = {
  el: {
    tagline: 'Επαγγελματικός Υπολογιστής Κόστους Ελάσματος. Υπολογίστε κόστη κατασκευής για ελάσματα και εξαρτήματα CNC router με ακρίβεια και ευκολία.',
    location: 'Ελλάδα',
    product: 'Προϊόν',
    solutions: 'Λύσεις',
    company: 'Εταιρεία',
    resources: 'Πόροι',
    copyright: 'Με επιφύλαξη παντός δικαιώματος.',
    links: {
      product: [
        { name: 'Λειτουργίες', path: '/features' },
        { name: 'Τιμολόγηση', path: '/pricing' },
        { name: 'Λήψη', path: '/download' },
        { name: 'Υποστήριξη', path: '/support' },
      ],
      solutions: [
        { name: 'Εκτίμηση Κόστους Κατασκευής', path: '/manufacturing-cost-estimator' },
        { name: 'Δωρεάν Υπολογιστής Κόστους', path: '/cost-calculator' },
        { name: 'Κοστολόγηση Μεταλλικών Εξαρτημάτων', path: '/metal-parts-costing' },
        { name: 'Κοστολόγηση Φρέζας CNC', path: '/cnc-milling-costing' },
        { name: 'Οδηγός Κοστολόγησης Λαμαρίνας', path: '/sheet-metal-costing' },
        { name: 'Κοστολόγηση Παραγωγής', path: '/production-costing' },
        { name: 'Κοστολόγηση Κάμψης', path: '/bend-costing' },
        { name: 'Κοστολόγηση Laser', path: '/laser-costing' },
        { name: 'Κοστολόγηση Σωλήνα Laser', path: '/tube-laser-costing' },
        { name: 'Εισαγωγή DXF & STEP', path: '/dxf-step-import' },
        { name: 'Plugin SolidWorks', path: '/solidworks-plugin' },
        { name: 'Plugin Inventor', path: '/inventor-plugin' },
        { name: 'Λογισμικό Προσφορών', path: '/quotation-software' },
      ],
      company: [
        { name: 'Σχετικά', path: '/about' },
        { name: 'Επικοινωνία', path: '/contact' },
        { name: 'Πολιτική Απορρήτου', path: '/privacy' },
        { name: 'Όροι Χρήσης', path: '/terms' },
      ],
      resources: [
        { name: 'Τεκμηρίωση', path: '/support' },
        { name: 'Εγχειρίδιο Χρήστη', path: '/support#manual' },
        { name: 'Βίντεο Οδηγοί', path: '/support#videos' },
        { name: 'Συχνές Ερωτήσεις', path: '/support#faq' },
      ],
    },
  },
  en: {
    tagline: 'Professional Sheet Metal Cost Estimator. Calculate manufacturing costs for sheet metal and CNC router parts with precision and ease.',
    location: 'Greece',
    product: 'Product',
    solutions: 'Solutions',
    company: 'Company',
    resources: 'Resources',
    copyright: 'All rights reserved.',
    links: {
      product: [
        { name: 'Features', path: '/features' },
        { name: 'Pricing', path: '/pricing' },
        { name: 'Download', path: '/download' },
        { name: 'Support', path: '/support' },
      ],
      solutions: [
        { name: 'Manufacturing Cost Estimator', path: '/manufacturing-cost-estimator' },
        { name: 'Free Cost Calculator', path: '/cost-calculator' },
        { name: 'Metal Parts Costing', path: '/metal-parts-costing' },
        { name: 'CNC Milling Costing', path: '/cnc-milling-costing' },
        { name: 'Sheet Metal Costing Guide', path: '/sheet-metal-costing' },
        { name: 'Production Costing', path: '/production-costing' },
        { name: 'Bend Costing', path: '/bend-costing' },
        { name: 'Laser Costing', path: '/laser-costing' },
        { name: 'Tube Laser Costing', path: '/tube-laser-costing' },
        { name: 'DXF & STEP Import', path: '/dxf-step-import' },
        { name: 'SolidWorks Plugin', path: '/solidworks-plugin' },
        { name: 'Inventor Plugin', path: '/inventor-plugin' },
        { name: 'Quotation Software', path: '/quotation-software' },
      ],
      company: [
        { name: 'About Us', path: '/about' },
        { name: 'Contact', path: '/contact' },
        { name: 'Privacy Policy', path: '/privacy' },
        { name: 'Terms of Service', path: '/terms' },
      ],
      resources: [
        { name: 'Documentation', path: '/support' },
        { name: 'User Manual', path: '/support#manual' },
        { name: 'Video Tutorials', path: '/support#videos' },
        { name: 'FAQ', path: '/support#faq' },
      ],
    },
  },
}

const Footer = () => {
  const { lang } = useLanguage()
  const t = translations[lang]
  const currentYear = new Date().getFullYear()

  return (
    <footer className="bg-gray-900 text-gray-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8 mb-8">
          {/* Brand */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center mb-4">
              <img
                src={`${import.meta.env.BASE_URL}images/logo-footer-white.png`}
                alt="AutoMechLabs Workshop Assistant"
                className="h-14 w-auto"
              />
            </Link>
            <p className="text-sm text-gray-400 mb-6 max-w-md">{t.tagline}</p>
            <div className="space-y-3">
              <a href="mailto:contact@automechlabs.com" className="flex items-center space-x-2 text-sm hover:text-primary-400 transition-colors">
                <Mail className="h-4 w-4" /><span>contact@automechlabs.com</span>
              </a>
              <a href="tel:+306951956643" className="flex items-center space-x-2 text-sm hover:text-primary-400 transition-colors">
                <Phone className="h-4 w-4" /><span>+30 695 195 6643</span>
              </a>
              <div className="flex items-center space-x-2 text-sm">
                <MapPin className="h-4 w-4" /><span>{t.location}</span>
              </div>
            </div>
          </div>

          {/* Product */}
          <div>
            <h3 className="text-white font-semibold mb-4">{t.product}</h3>
            <ul className="space-y-2">
              {t.links.product.map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-sm hover:text-primary-400 transition-colors">{link.name}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Solutions */}
          <div>
            <h3 className="text-white font-semibold mb-4">{t.solutions}</h3>
            <ul className="space-y-2">
              {t.links.solutions.map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-sm hover:text-primary-400 transition-colors">{link.name}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-white font-semibold mb-4">{t.company}</h3>
            <ul className="space-y-2">
              {t.links.company.map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-sm hover:text-primary-400 transition-colors">{link.name}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="text-white font-semibold mb-4">{t.resources}</h3>
            <ul className="space-y-2">
              {t.links.resources.map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-sm hover:text-primary-400 transition-colors">{link.name}</Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm text-gray-400">© {currentYear} Workshop Assistant. {t.copyright}</p>
            <div className="flex space-x-4">
              <a href="https://www.linkedin.com/in/workshop-assistant-automechlabs/" target="_blank" rel="noopener noreferrer" className="p-2 bg-gray-800 rounded-lg hover:bg-primary-600 transition-colors" aria-label="LinkedIn"><Linkedin className="h-5 w-5" /></a>
              <a href="https://www.youtube.com/@AutoMechLabs-WA" target="_blank" rel="noopener noreferrer" className="p-2 bg-gray-800 rounded-lg hover:bg-primary-600 transition-colors" aria-label="YouTube"><Youtube className="h-5 w-5" /></a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
